// App entry: wires the map, live location, sheet, list and card together.
import { Map as MapLibreMap, addProtocol, setWorkerUrl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// MapLibre 6 runs its parser in a module worker; let Vite bundle it and hand MapLibre the URL.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import '@fontsource/libre-caslon-text/400.css';
import '@fontsource/libre-caslon-text/700.css';
import './styles.css';

import { buildStyle } from './map/style.js';
import { registerTiles } from './map/tiles.js';
import {
  addSiteLayers, setSiteData, setSelected, setVisited, onSiteTap, addPreviewLine, setPreviewLine,
} from './map/sites.js';
import { createLocation } from './map/location.js';
import { startCompass } from './map/heading.js';
import { createSheet } from './ui/sheet.js';
import { renderCard } from './ui/card.js';
import { createNearby } from './ui/nearby.js';
import { chooseApp, forgetApp } from './ui/maps-chooser.js';
import { icon } from './ui/dom.js';
import { ICONS } from './ui/icons.js';
import { sortByDistance, filterSites } from './lib/nearby.js';
import { distanceM } from './lib/geo.js';
import { directionsUrl } from './lib/links.js';
import { load, save } from './lib/storage.js';

const $ = (id) => document.getElementById(id);

function toast(msg, ms = 4500) {
  const t = $('toast');
  t.textContent = msg;
  t.hidden = false;
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => { t.hidden = true; }, ms);
}

async function getJson(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
  return r.json();
}

async function main() {
  const [collection, geojson] = await Promise.all([getJson('/data/collection.json'), getJson('/data/sites.geojson')]);
  const sites = geojson.features.map((f) => ({ ...f.properties, lon: f.geometry.coordinates[0], lat: f.geometry.coordinates[1] }));
  const bySlug = new Map(sites.map((s) => [s.slug, s]));
  const categories = collection.categories;
  const catBy = new Map(categories.map((c) => [c.slug, c]));

  const state = {
    selected: null,
    query: '',
    active: new Set(),
    visited: new Set(load('visited', [])),
    locating: false,
  };

  // ---- Map
  setWorkerUrl(maplibreWorkerUrl);
  const origin = window.location.origin;
  const tilesUrl = `${origin}/tiles/${collection.slug}.pmtiles`;
  registerTiles(addProtocol, tilesUrl);
  const b = collection.bbox;
  const map = new MapLibreMap({
    container: 'map',
    style: buildStyle({ tilesUrl, glyphsUrl: `${origin}/fonts/{fontstack}/{range}.pbf` }),
    center: [collection.center.lon, collection.center.lat],
    zoom: 16,
    minZoom: 11,
    maxZoom: 19,
    maxBounds: [[b.west - 0.05, b.south - 0.05], [b.east + 0.05, b.north + 0.05]],
    dragRotate: false,
    pitchWithRotate: false,
    touchPitch: false,
    attributionControl: { compact: true },
  });
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();

  // ---- Sheet and panels
  const sheet = createSheet({
    sheet: $('sheet'), grip: $('sheet-grip'), handle: $('sheet-handle'),
    onChange: (_, visible) => {
      $('map-controls').style.bottom = `calc(${visible}px + 12px + env(safe-area-inset-bottom))`;
    },
  });
  const body = $('sheet-body');

  const nearby = createNearby(categories, {
    onSelect: (slug) => select(slug, { fly: true }),
    onQuery: (q) => { state.query = q; refreshList(); },
    onToggleCategory: (slug) => {
      if (state.active.has(slug)) state.active.delete(slug); else state.active.add(slug);
      refreshList();
      refreshPins();
    },
    onLocate: () => startLocating(),
  });

  const currentSpot = () => {
    const p = tracker.position;
    return p?.inArea ? p : null;
  };

  function visibleSites() {
    return filterSites(sites, { query: state.query, categories: state.active });
  }

  function refreshList() {
    const sorted = sortByDistance(visibleSites(), currentSpot());
    nearby.update({
      sites: sorted, total: sites.length, active: state.active, haveLocation: !!currentSpot(),
      locating: state.locating, visited: state.visited,
    });
  }

  function refreshPins() {
    const keep = new Set(filterSites(sites, { categories: state.active }).map((s) => s.slug));
    setSiteData(map, { type: 'FeatureCollection', features: geojson.features.filter((f) => keep.has(f.properties.slug)) });
  }

  function showNearby() {
    if (state.selected) setSelected(map, state.selected, null);
    state.selected = null;
    setPreviewLine(map, null, null);
    body.replaceChildren(nearby.el);
    refreshList();
  }

  function renderSelected() {
    const site = bySlug.get(state.selected);
    if (!site) return;
    const here = currentSpot();
    const distance = here ? distanceM(here.lat, here.lon, site.lat, site.lon) : null;
    body.replaceChildren(renderCard(site, {
      category: catBy.get(site.category),
      distance,
      visited: state.visited.has(site.slug),
      onBack: () => { showNearby(); sheet.set('peek'); },
      onDirections: async (mode) => {
        const app = await chooseApp($('maps-dialog'));
        if (app) window.open(directionsUrl(app, mode, site.lat, site.lon), '_blank', 'noopener');
      },
      onToggleVisited: () => {
        if (state.visited.has(site.slug)) {
          state.visited.delete(site.slug);
          map.setFeatureState({ source: 'sites', id: site.slug }, { visited: false });
        } else {
          state.visited.add(site.slug);
          setVisited(map, [site.slug]);
        }
        save('visited', [...state.visited]);
        renderSelected();
      },
      onChangeMapsApp: () => { forgetApp(); toast('Next time you tap Walk or Drive, you can pick a maps app.'); },
      onExpand: () => { if (sheet.state !== 'full') sheet.set('full'); },
    }));
    setPreviewLine(map, here, site);
  }

  function select(slug, { fly = false } = {}) {
    const site = bySlug.get(slug);
    if (!site) return;
    setSelected(map, state.selected, slug);
    state.selected = slug;
    renderSelected();
    body.scrollTop = 0;
    if (sheet.state === 'peek' || sheet.state === 'full') sheet.set('half');
    tracker.setFollow(false);
    const padding = sheet.wide ? { left: 400, bottom: 0 } : { bottom: sheet.visibleHeight(), left: 0 };
    map.easeTo({ center: [site.lon, site.lat], zoom: fly ? Math.max(map.getZoom(), 17) : map.getZoom(), padding, duration: 600 });
    body.querySelector('.card-title')?.focus?.();
  }

  // ---- Location
  const locateBtn = $('btn-locate');
  const tracker = createLocation(map, {
    bbox: collection.bbox,
    onPosition: () => {
      state.locating = false;
      if (state.selected) renderSelected(); else refreshList();
    },
    onStatus: (s) => {
      if (s.error === 'denied') toast('Location is turned off for this site. You can still browse the map and list.');
      else if (s.error === 'unavailable') toast('Can’t find your location right now. Trying again…');
      else if (s.error === 'unsupported') toast('This browser can’t share your location.');
      if (s.inArea === false) toast('You’re outside Saugatuck and Douglas. The map stays here; your dot shows when you’re nearby.');
      locateBtn.dataset.state = s.follow ? 'follow' : s.active ? 'on' : 'off';
      locateBtn.replaceChildren(icon(s.follow ? ICONS.locateFollow : ICONS.locate));
      locateBtn.setAttribute('aria-label', s.follow ? 'Following your location' : 'Show my location');
      locateBtn.setAttribute('aria-pressed', String(!!s.follow));
      if (s.error) { state.locating = false; refreshList(); }
    },
  });

  let compassAsked = false;
  async function startLocating() {
    state.locating = !tracker.position;
    tracker.start();
    refreshList();
    if (!compassAsked) {
      compassAsked = true;
      const r = await startCompass((deg) => tracker.setHeading(deg));
      if (r === 'denied') toast('Compass not allowed, so the dot won’t show which way you face.');
    }
  }

  locateBtn.replaceChildren(icon(ICONS.locate));
  locateBtn.addEventListener('click', () => {
    if (tracker.active && tracker.position) tracker.setFollow(true);
    else startLocating();
  });
  $('btn-list').replaceChildren(icon(ICONS.list));
  $('btn-list').addEventListener('click', () => { showNearby(); sheet.set('full'); });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.selected && !$('maps-dialog').open) { showNearby(); sheet.set('peek'); }
  });

  // ---- Data layers once the style is ready
  map.on('load', () => {
    addSiteLayers(map, collection, geojson);
    addPreviewLine(map);
    onSiteTap(map, (slug) => select(slug));
    setVisited(map, state.visited);
    if (state.selected) { setSelected(map, null, state.selected); renderSelected(); }
  });

  showNearby();

  // Start tracking without a tap only if permission was already granted (no surprise prompt).
  try {
    const perm = await navigator.permissions?.query({ name: 'geolocation' });
    if (perm?.state === 'granted') tracker.start();
  } catch { /* Permissions API not available; wait for a tap */ }

  if (import.meta.env.DEV) {
    window.__fieldGuide = { map, tracker, select, simulate: (...a) => tracker.simulate(...a) };
  }
}

main().catch((e) => {
  console.error(e);
  toast('The map couldn’t load. Check your connection and reload.', 60000);
});
